/**
 * Adaptador para <video> nativo (y cualquier player que lo envuelva:
 * VideoJS, Plyr, Cloudflare Stream, Wistia embebido…).
 *
 * La contabilidad la lleva el reloj central de core.ts vía `probe()`; aquí
 * solo se traducen los eventos del elemento a llamadas de la sesión.
 */
import { VideoSession, observeVisibility, config } from './core';

type VideoElWithState = HTMLVideoElement & { __trkVideo?: VideoSession };

let autoCounter = 0;

function fileNameFrom(src: string): string | null {
  try {
    const u = new URL(src, location.href);
    return u.pathname.split('/').filter(Boolean).pop() ?? null;
  } catch {
    return null;
  }
}

function finiteOrNull(n: number): number | null {
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Props declarativas: data-video-id / -title / -webinar-id / -live. */
function metaFromDataset(el: HTMLElement): { webinarId: string | null; live: boolean; props: Record<string, unknown> } {
  const props: Record<string, unknown> = {};
  const collection = el.dataset.videoCollection || el.dataset.videoGroup;
  if (collection) props.collection = collection;
  return {
    webinarId: el.dataset.webinarId || null,
    live: el.dataset.videoLive === 'true' || el.hasAttribute('data-video-live'),
    props,
  };
}

/** Instrumenta un <video> concreto. Idempotente por elemento. */
export function attach(el: HTMLVideoElement, meta?: { id?: string; title?: string }): VideoSession | null {
  const target = el as VideoElWithState;
  if (target.__trkVideo) return target.__trkVideo;
  if (el.hasAttribute('data-track-ignore')) return null;

  const src = el.currentSrc || el.src || el.querySelector('source')?.src || null;
  const id =
    meta?.id ||
    el.dataset.videoId ||
    el.id ||
    (src ? fileNameFrom(src) : null) ||
    `video-${++autoCounter}`;

  const extra = metaFromDataset(el);
  const session = new VideoSession({
    id,
    title: meta?.title || el.dataset.videoTitle || el.title || null,
    src,
    duration: finiteOrNull(el.duration),
    player: 'html5',
    el,
    ...extra,
  });
  target.__trkVideo = session;

  session.probe = () => ({
    position: el.currentTime,
    // `readyState < 3` = buffering: el reloj sigue corriendo pero no hay
    // imagen, así que no cuenta como visionado.
    playing: !el.paused && !el.ended && el.readyState >= 3,
  });
  session.muted = el.muted || el.volume === 0;
  session.rate = el.playbackRate || 1;

  observeVisibility(session);
  session.impression();

  el.addEventListener('durationchange', () => {
    session.setDuration(finiteOrNull(el.duration));
    if (!session.src) session.src = el.currentSrc || el.src || null;
  });
  el.addEventListener('play', () => session.play(el.currentTime));
  el.addEventListener('playing', () => session.play(el.currentTime));
  el.addEventListener('pause', () => { if (!el.ended) session.pause(el.currentTime); });
  el.addEventListener('ended', () => session.ended());
  el.addEventListener('seeking', () => session.seek(el.currentTime));
  el.addEventListener('ratechange', () => session.setRate(el.playbackRate));
  el.addEventListener('volumechange', () => session.setMuted(el.muted || el.volume === 0));
  el.addEventListener('error', () => {
    const err = el.error;
    session.error(err?.code ?? 'unknown', err?.message);
  });
  // El timeupdate (~4/s) afina la posición entre pulsos del reloj.
  el.addEventListener('timeupdate', () => {
    if (!el.paused) session.tick({ position: el.currentTime, playing: true });
  });
  document.addEventListener('fullscreenchange', () => {
    session.setFullscreen(document.fullscreenElement === el || el.contains(document.fullscreenElement));
  });

  if (config.debug) console.debug('[trackrift-video] <video> instrumentado:', id);
  return session;
}

/** Instrumenta todos los <video> bajo `root`. Devuelve cuántos nuevos. */
export function scanVideos(root: ParentNode = document): number {
  let n = 0;
  root.querySelectorAll?.('video').forEach((el) => {
    const before = (el as VideoElWithState).__trkVideo;
    attach(el as HTMLVideoElement);
    if (!before && (el as VideoElWithState).__trkVideo) n += 1;
  });
  return n;
}
